import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import usePatientOrderHistory from '@/lib/api/med-requests/order-history'
import { afterApprovalStatuses } from '@/lib/constants/med-requests'
import type { MedRequest } from '@/types/data'
import { addWeeks, subWeeks } from 'date-fns'
import { useMemo } from 'react'

/**
 * The patient's latest approved order of this category in the last 12 weeks (`lastIssuedDate`), and
 * 12 weeks after it (`advancedPreviousOrderDate`) for the "set to 12 weeks" shortcut.
 */
export function usePreviousOrderDates() {
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { data: previousOrders } = usePatientOrderHistory(medRequestData.patient.id ?? '', !!medRequestData.patient.id)

  return useMemo(() => {
    if (!previousOrders) {
      return { advancedPreviousOrderDate: null, lastIssuedDate: null }
    }

    const getOrderDate = (order: MedRequest) => {
      return new Date(order.lastActionTakenAt || order.lastUpdateOn || order.creationDate)
    }

    // Skips the request's own order (e.g. an item approved on its card); V1 rows have no order hash.
    const isSameOrder = (order: MedRequest) =>
      order.id === medRequestData?.id || (!!medRequestData?.orderHash && order.orderHash === medRequestData.orderHash)

    const filteredPreviousOrders = previousOrders
      .filter(order => !isSameOrder(order))
      .sort((a, b) => getOrderDate(b).getTime() - getOrderDate(a).getTime())

    const twelveWeeksAgo = subWeeks(new Date(), 12)
    let lastIssuedDate: Date | null = null
    const advancedSetDate = filteredPreviousOrders.find(order => {
      const orderDate = getOrderDate(order)
      const findResult =
        afterApprovalStatuses.map(entry => entry.toLowerCase()).includes(order.stage.toLowerCase()) &&
        order.orderedItem.category === medRequestData?.orderedItem.category &&
        orderDate.getTime() > twelveWeeksAgo.getTime()

      if (findResult) {
        lastIssuedDate = orderDate
      }

      return findResult
    })

    if (advancedSetDate) {
      return {
        advancedPreviousOrderDate: addWeeks(getOrderDate(advancedSetDate), 12),
        lastIssuedDate: lastIssuedDate as Date | null
      }
    }

    return { advancedPreviousOrderDate: null, lastIssuedDate: null }
  }, [previousOrders, medRequestData])
}
